import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import * as allauthApi from '../services/allauthApi';
import api from '../services/api';
import apiEndpoints from '../services/api/endpoints';
import { getWeaversWorldApiUrl, logger } from '../services/utils';
import websocketService from '../services/websocketService';
import { authState as authStateService } from '../services/authState';

// Create context
export const AuthContext = createContext(null);

// Initial state
const initialState = {
  isAuthenticated: false,
  user: null,
  userProfile: null,
  childProfiles: [],
  activeChildProfile: null,
  loading: true,
  error: null,
  authFlows: [],     // Available authentication flows (login, signup, 2fa, etc.)
  pendingFlow: null,  // Any pending flow that needs completion
  // Anonymous session tracking
  anonymousActivity: {
    storiesCreated: 0,
    charactersCreated: 0,
    lastVisit: null,
    hasContent: false
  }
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(initialState);
  
  // Load anonymous activity from localStorage on mount
  useEffect(() => {
    const savedActivity = localStorage.getItem('anonymousActivity');
    if (savedActivity) {
      try {
        const parsedActivity = JSON.parse(savedActivity);
        setAuthState(prev => ({
          ...prev,
          anonymousActivity: {
            ...prev.anonymousActivity,
            ...parsedActivity,
            lastVisit: new Date().toISOString() // Update last visit on load
          }
        }));
        logger.trace('Auth', 'Loaded anonymous activity from localStorage', parsedActivity);
      } catch (error) {
        logger.error('Auth', 'Failed to parse anonymous activity from localStorage', error);
      }
    }
  }, []);
  
  // Save anonymous activity to localStorage when it changes
  useEffect(() => {
    if (!authState.isAuthenticated) {
      localStorage.setItem('anonymousActivity', JSON.stringify(authState.anonymousActivity));
      logger.trace('Auth', 'Saved anonymous activity to localStorage', authState.anonymousActivity);
    }
  }, [authState.anonymousActivity, authState.isAuthenticated]);
  
  // We're now importing and using the properly configured API client
  // that includes CSRF token handling
  
  // Load authentication state
  const loadAuthState = useCallback(async () => {
    try {
      logger.trace('Auth', 'Loading authentication state');
      setAuthState(prev => ({ ...prev, loading: true }));
      
      // Get current session status
      const sessionData = await allauthApi.getSession();
      logger.trace('Auth', 'Session data', sessionData);
      
      if (sessionData.meta?.is_authenticated) {
        // User is authenticated - get basic info from session
        let userData = sessionData.data.user;
        logger.trace('Auth', 'User is authenticated from session', userData);
        
        // Load detailed user info with all fields including date_joined
        try {
          const userResponse = await apiEndpoints.auth.getCurrentUser();
          if (userResponse?.data) {
            // Merge detailed info with session user info
            userData = {
              ...userData,
              ...userResponse.data,
            };
            logger.trace('Auth', 'User data enhanced with details', userData);
          }
        } catch (userDataError) {
          logger.warn('Auth', 'Failed to load detailed user data', userDataError);
          // Continue with basic user data from session
        }
        
        // Load child profiles from Weaver's World API
        try {
          const childProfilesResponse = await api.get('/child-profiles/');
          logger.trace('Auth', 'Child profiles loaded', childProfilesResponse.data);
          
          // Check if there's an active profile ID in the session
          const activeProfileIdInSession = sessionData.data.active_child_profile_id;
          logger.trace('Auth', 'Active profile ID in session', activeProfileIdInSession);
          
          // Find the active profile from the loaded profiles
          let activeProfile = null;
          
          // First try: Use active profile ID from session
          if (activeProfileIdInSession && childProfilesResponse.data.length > 0) {
            activeProfile = childProfilesResponse.data.find(p => p.id === activeProfileIdInSession);
            if (activeProfile) {
              logger.debug('Auth', 'Using active profile from session');
            } else {
              logger.warn('Auth', `Session indicated active profile ID ${activeProfileIdInSession} but it wasn't found in loaded profiles`);
            }
          }
          
          // Second try: If no active profile found in session but we had one previously, try to maintain it
          if (!activeProfile && authState.activeChildProfile && childProfilesResponse.data.length > 0) {
            const prevActiveId = authState.activeChildProfile.id;
            // Try to find the previously active profile by ID
            activeProfile = childProfilesResponse.data.find(p => p.id === prevActiveId);
            
            if (activeProfile) {
              logger.debug('Auth', 'Maintaining previously active profile');
            } else {
              logger.debug('Auth', 'Previously active profile no longer exists in available profiles');
            }
          }
          
          // Third try: If still no active profile found, use the first one as fallback
          if (!activeProfile && childProfilesResponse.data.length > 0) {
            activeProfile = childProfilesResponse.data[0];
            logger.debug('Auth', 'Using first profile as fallback');
          }
          
          // ALWAYS explicitly set/confirm the active profile on the server
          // This ensures backend session and frontend state are in sync
          if (activeProfile) {
            try {
              const setActiveResponse = await api.post(`/child-profiles/${activeProfile.id}/set-active/`);
              logger.info('Auth', `Explicitly activated profile during auth refresh: ${activeProfile.name} (ID: ${activeProfile.id})`, setActiveResponse.data);
            } catch (setActiveError) {
              logger.error('Auth', `Failed to explicitly activate profile during auth refresh: ${activeProfile.name} (ID: ${activeProfile.id})`, setActiveError);
            }
          }
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null, // Will load separately if needed
            childProfiles: childProfilesResponse.data,
            activeChildProfile: activeProfile,
            loading: false,
            error: null,
            authFlows: sessionData.data?.flows || [],
            pendingFlow: null,
          });
        } catch (profileError) {
          // If we can't load child profiles, still set the authenticated state
          logger.error('Auth', 'Failed to load child profiles', profileError);
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: [],
            activeChildProfile: null,
            loading: false,
            error: 'Failed to load child profiles',
            authFlows: sessionData.data?.flows || [],
            pendingFlow: null,
          });
        }
      } else {
        // User is not authenticated
        logger.trace('Auth', 'User is not authenticated');
        setAuthState({
          ...initialState,
          loading: false,
          authFlows: sessionData.data?.flows || [],
          pendingFlow: sessionData.data?.flows?.find(flow => flow.is_pending) || null,
        });
      }
    } catch (error) {
      logger.error('Auth', 'Failed to load auth state', error);
      setAuthState({
        ...initialState,
        loading: false,
        error: 'Failed to load authentication state'
      });
    }
  }, []);
  
  // Load auth state on mount
  useEffect(() => {
    loadAuthState();
  }, [loadAuthState]);
  
  // Update centralized authState when context state changes
  useEffect(() => {
    // Only update when loading is false to ensure we have complete state
    if (!authState.loading) {
      authStateService.updateState({
        isAuthenticated: authState.isAuthenticated,
        userId: authState.user?.id,
        profileId: authState.activeChildProfile?.id
      });
      
      logger.debug('AuthContext', 'Updated centralized authState with current auth information', {
        isAuthenticated: authState.isAuthenticated,
        userId: authState.user?.id,
        profileId: authState.activeChildProfile?.id
      });
    }
  }, [authState.isAuthenticated, authState.user, authState.activeChildProfile, authState.loading]);
  
  // Login function
  const login = async (email, password) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      // The CSRF token is now automatically handled by the allauthApiClient
      const response = await allauthApi.login(email, password);
      
      // Check if we're authenticated now
      if (response.meta?.is_authenticated) {
        // Successfully authenticated - get basic user data from response
        let userData = response.data.user;
        
        // Load detailed user info with all fields including date_joined
        try {
          const userResponse = await apiEndpoints.auth.getCurrentUser();
          if (userResponse?.data) {
            // Merge detailed info with session user info
            userData = {
              ...userData,
              ...userResponse.data,
            };
            logger.trace('Auth', 'User data enhanced with details during login', userData);
          }
        } catch (userDataError) {
          logger.warn('Auth', 'Failed to load detailed user data during login', userDataError);
          // Continue with basic user data from session
        }
        
        // Load child profiles
        try {
          const childProfilesResponse = await api.get('/child-profiles/');
          
          // Check if session has an active child profile ID
          let activeChildProfile = null;
          const activeProfileId = response.data?.active_child_profile_id;
          
          if (activeProfileId && childProfilesResponse.data.length > 0) {
            // Find the matching profile by ID
            activeChildProfile = childProfilesResponse.data.find(p => p.id === activeProfileId);
            logger.info('Auth', `Found active child profile from session: ${activeChildProfile?.name} (ID: ${activeProfileId})`);
          }
          
          // Fallback to first profile if no active profile found
          if (!activeChildProfile && childProfilesResponse.data.length > 0) {
            activeChildProfile = childProfilesResponse.data[0];
            logger.info('Auth', `Using first profile as fallback: ${activeChildProfile.name}`);
          }
          
          // ALWAYS explicitly set/confirm the active profile on the server after login
          // This ensures backend session and frontend state are in sync
          if (activeChildProfile) {
            try {
              const setActiveResponse = await api.post(`/child-profiles/${activeChildProfile.id}/set-active/`);
              logger.info('Auth', `Explicitly activated profile after login: ${activeChildProfile.name} (ID: ${activeChildProfile.id})`, setActiveResponse.data);
              
              // Update WebSocket with authentication parameters
              if (userData && userData.id) {
                try {
                  const authParams = {
                    userId: userData.id,
                    profileId: activeChildProfile.id
                  };
                  
                  // Update auth parameters and reconnect if needed
                  await websocketService.updateAuthParams(authParams);
                  logger.info('Auth', 'WebSocket authentication parameters updated after login');
                } catch (wsError) {
                  logger.error('Auth', 'Failed to update WebSocket auth after login', wsError);
                  // Continue anyway - the login was successful
                }
              }
            } catch (setActiveError) {
              logger.error('Auth', `Failed to explicitly activate profile after login: ${activeChildProfile.name} (ID: ${activeChildProfile.id})`, setActiveError);
            }
          }
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null, // Will load separately if needed
            childProfiles: childProfilesResponse.data,
            activeChildProfile,
            loading: false,
            error: null,
            authFlows: [],
            pendingFlow: null,
            anonymousActivity: initialState.anonymousActivity, // Reset anonymous activity
          });
          
          return { success: true };
        } catch (profileError) {
          logger.error('Auth', 'Failed to load child profiles', profileError);
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: [],
            activeChildProfile: null,
            loading: false,
            error: 'Failed to load child profiles',
            authFlows: [],
            pendingFlow: null,
          });
          
          return { success: true, profileError: true };
        }
      } else {
        // There are pending flows to complete (2FA, email verification, etc.)
        const pendingFlow = response.data?.flows?.find(flow => flow.is_pending);
        
        setAuthState(prev => ({
          ...prev,
          loading: false,
          authFlows: response.data?.flows || [],
          pendingFlow,
        }));
        
        return { success: false, pendingFlow };
      }
    } catch (error) {
      logger.error('Auth', 'Login failed', error);
      
      // Extract error message from API response
      let errorMessage = 'Login failed. Please check your credentials.';
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].message;
      }
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      throw error;
    }
  };
  
  // Logout function
  const logout = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      // Record if we're on a protected page that needs redirection after logout
      const currentPath = window.location.pathname;
      const needsRedirect = currentPath.includes('/weaversworld/story/') || 
                           currentPath.includes('/profile') || 
                           currentPath.includes('/child-profiles/');
      
      // First reset the WebSocket session to leave any joined stories
      // This ensures we leave story channels before disconnecting
      logger.info('Auth', 'Resetting WebSocket session before logout');
      websocketService.resetSession();
      
      // Clear the auth parameters
      websocketService.updateAuthParams(null);
      
      // Then disconnect WebSocket to reset all state between user sessions
      // This prevents issues where WebSocket maintains state from previous user
      logger.info('Auth', 'Disconnecting WebSocket before logout');
      websocketService.disconnect();
      
      // Now handle the actual logout
      await allauthApi.logout();
      
      // Reset auth state and load available auth flows
      const sessionData = await allauthApi.getSession();
      
      // Dispatch an event to force StoryContext to reset completely
      // This ensures no cached story data is shown after logout
      document.dispatchEvent(new CustomEvent('forceResetAllContexts', {
        detail: { 
          reason: 'logout',
          timestamp: Date.now()
        }
      }));
      
      // Log the event dispatch
      logger.info('Auth', 'Dispatched forceResetAllContexts event to clear story data on logout');
      
      setAuthState({
        ...initialState,
        loading: false,
        authFlows: sessionData.data?.flows || [],
        pendingFlow: null,
      });
      
      // After the logout is complete, redirect if needed
      if (needsRedirect) {
        setTimeout(() => {
          // Use history API to navigate without full page reload
          if (window.history && window.history.pushState) {
            window.history.pushState({}, '', '/weaversworld');
            // Create and dispatch a PopStateEvent to trigger route change
            window.dispatchEvent(new PopStateEvent('popstate'));
          } else {
            // Fallback for older browsers
            window.location.pathname = '/weaversworld';
          }
        }, 10); // Very small delay to allow state to update first
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Auth', 'Logout failed', error);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Logout failed'
      }));
      throw error;
    }
  };
  
  // Register function
  const register = async (email, password1, password2) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      // The CSRF token is now automatically handled by the allauthApiClient
      const response = await allauthApi.signup(email, password1, password2);
      
      // Check authentication status after signup
      if (response.meta?.is_authenticated) {
        // Successfully authenticated after signup
        const userData = response.data.user;
        
        setAuthState({
          isAuthenticated: true,
          user: userData,
          userProfile: null,
          childProfiles: [],  // Will be empty for new user
          activeChildProfile: null,
          loading: false,
          error: null,
          authFlows: [],
          pendingFlow: null,
          anonymousActivity: initialState.anonymousActivity, // Reset anonymous activity for new users
        });
        
        return { success: true };
      } else {
        // There are pending flows (like email verification)
        const pendingFlow = response.data?.flows?.find(flow => flow.is_pending);
        
        setAuthState(prev => ({
          ...prev,
          loading: false,
          authFlows: response.data?.flows || [],
          pendingFlow,
        }));
        
        return { success: false, pendingFlow };
      }
    } catch (error) {
      logger.error('Auth', 'Registration failed', error);
      
      // Extract error message
      let errorMessage = 'Registration failed';
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].message;
      }
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      throw error;
    }
  };
  
  // Handle 2FA completion
  const completeTwoFactorAuth = async (code) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await allauthApi.completeTwoFactorAuth(code);
      
      // We should be authenticated now
      if (response.meta?.is_authenticated) {
        const userData = response.data.user;
        
        // Load child profiles
        try {
          const childProfilesResponse = await api.get('/child-profiles/');
          
          // Get the first profile (if any)
          let selectedProfile = childProfilesResponse.data.length > 0 ? 
            childProfilesResponse.data[0] : null;
          
          // ALWAYS explicitly set/confirm the active profile on the server
          if (selectedProfile) {
            try {
              const setActiveResponse = await api.post(`/child-profiles/${selectedProfile.id}/set-active/`);
              logger.info('Auth', `Explicitly activated profile after 2FA: ${selectedProfile.name} (ID: ${selectedProfile.id})`, setActiveResponse.data);
            } catch (setActiveError) {
              logger.error('Auth', `Failed to activate profile after 2FA: ${selectedProfile.name}`, setActiveError);
            }
          }
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: childProfilesResponse.data,
            activeChildProfile: selectedProfile,
            loading: false,
            error: null,
            authFlows: [],
            pendingFlow: null,
          });
          
          return { success: true };
        } catch (profileError) {
          logger.error('Auth', 'Failed to load child profiles', profileError);
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: [],
            activeChildProfile: null,
            loading: false,
            error: 'Failed to load child profiles',
            authFlows: [],
            pendingFlow: null,
          });
          
          return { success: true, profileError: true };
        }
      } else {
        // Something unexpected happened
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to complete two-factor authentication'
        }));
        
        return { success: false };
      }
    } catch (error) {
      logger.error('Auth', '2FA failed', error);
      
      // Extract error message
      let errorMessage = 'Two-factor authentication failed';
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].message;
      }
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      throw error;
    }
  };
  
  // Complete email verification
  const verifyEmailWithKey = async (key) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await allauthApi.verifyEmail(key);
      
      // We should be authenticated now
      if (response.meta?.is_authenticated) {
        const userData = response.data.user;
        
        // Load child profiles
        try {
          const childProfilesResponse = await api.get('/child-profiles/');
          
          // Get the first profile (if any)
          let selectedProfile = childProfilesResponse.data.length > 0 ? 
            childProfilesResponse.data[0] : null;
          
          // ALWAYS explicitly set/confirm the active profile on the server
          if (selectedProfile) {
            try {
              const setActiveResponse = await api.post(`/child-profiles/${selectedProfile.id}/set-active/`);
              logger.info('Auth', `Explicitly activated profile after email verification: ${selectedProfile.name} (ID: ${selectedProfile.id})`, setActiveResponse.data);
            } catch (setActiveError) {
              logger.error('Auth', `Failed to activate profile after email verification: ${selectedProfile.name}`, setActiveError);
            }
          }
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: childProfilesResponse.data,
            activeChildProfile: selectedProfile,
            loading: false,
            error: null,
            authFlows: [],
            pendingFlow: null,
          });
          
          return { success: true };
        } catch (profileError) {
          logger.error('Auth', 'Failed to load child profiles', profileError);
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: [],
            activeChildProfile: null,
            loading: false,
            error: 'Failed to load child profiles',
            authFlows: [],
            pendingFlow: null,
          });
          
          return { success: true, profileError: true };
        }
      } else {
        // Something unexpected happened
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to verify email'
        }));
        
        return { success: false };
      }
    } catch (error) {
      logger.error('Auth', 'Email verification failed', error);
      
      // Extract error message
      let errorMessage = 'Email verification failed';
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].message;
      }
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      throw error;
    }
  };
  
  // Request password reset
  const requestPasswordReset = async (email) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await allauthApi.requestPasswordReset(email);
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
      }));
      
      return { success: true };
    } catch (error) {
      logger.error('Auth', 'Password reset request failed', error);
      
      // Extract error message
      let errorMessage = 'Password reset request failed';
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].message;
      }
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      throw error;
    }
  };
  
  // Reset password with key
  const resetPassword = async (key, password) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await allauthApi.resetPassword(key, password);
      
      // Check if we're authenticated now
      if (response.meta?.is_authenticated) {
        // Successfully authenticated
        const userData = response.data.user;
        
        // Load child profiles
        try {
          const childProfilesResponse = await api.get('/child-profiles/');
          
          // Get the first profile (if any)
          let selectedProfile = childProfilesResponse.data.length > 0 ? 
            childProfilesResponse.data[0] : null;
          
          // ALWAYS explicitly set/confirm the active profile on the server
          if (selectedProfile) {
            try {
              const setActiveResponse = await api.post(`/child-profiles/${selectedProfile.id}/set-active/`);
              logger.info('Auth', `Explicitly activated profile after password reset: ${selectedProfile.name} (ID: ${selectedProfile.id})`, setActiveResponse.data);
            } catch (setActiveError) {
              logger.error('Auth', `Failed to activate profile after password reset: ${selectedProfile.name}`, setActiveError);
            }
          }
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: childProfilesResponse.data,
            activeChildProfile: selectedProfile,
            loading: false,
            error: null,
            authFlows: [],
            pendingFlow: null,
          });
          
          return { success: true };
        } catch (profileError) {
          logger.error('Auth', 'Failed to load child profiles', profileError);
          
          setAuthState({
            isAuthenticated: true,
            user: userData,
            userProfile: null,
            childProfiles: [],
            activeChildProfile: null,
            loading: false,
            error: 'Failed to load child profiles',
            authFlows: [],
            pendingFlow: null,
          });
          
          return { success: true, profileError: true };
        }
      } else {
        // Something unexpected happened
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to reset password'
        }));
        
        return { success: false };
      }
    } catch (error) {
      logger.error('Auth', 'Password reset failed', error);
      
      // Extract error message
      let errorMessage = 'Password reset failed';
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].message;
      }
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      throw error;
    }
  };
  
  // Create child profile function (using Weaver's World API)
  const createChildProfile = async (name, age = null) => {
    try {
      logger.trace('Auth', 'Creating child profile', { name, age });
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      // Use the main API instance with CSRF token handling
      const response = await api.post('/child-profiles/', {
        name,
        age,
      });
      
      const newProfile = response.data;
      logger.trace('Auth', 'Child profile created', newProfile);
      
      // Also set this as the active profile in the backend
      try {
        logger.trace('Auth', 'Setting profile as active in backend', { profileId: newProfile.id });
        const setActiveResponse = await api.post(`/child-profiles/${newProfile.id}/set-active/`);
        logger.trace('Auth', 'Set active profile response', setActiveResponse.data);
        
        // Log the session key and any other useful information from the response
        if (setActiveResponse.data.session_key) {
          logger.trace('Auth', `Session key from backend: ${setActiveResponse.data.session_key}`);
        }
        
        // Log all cookies for debugging
        logger.trace('Auth', 'Cookies after setting active profile', document.cookie);
        
        // Get the session data to ensure session is updated
        const sessionResponse = await allauthApi.getSession();
        logger.trace('Auth', 'Session after setting active profile', sessionResponse);
        
        // Additional logging of important state
        logger.info('Auth', 'New child profile set as active', {
          profileId: newProfile.id,
          profileName: newProfile.name,
          sessionData: setActiveResponse.data
        });
      } catch (setActiveError) {
        logger.error('Auth', 'Failed to set active profile in backend', setActiveError);
        // Continue anyway - the profile was created successfully
      }
      
      setAuthState(prev => ({
        ...prev,
        childProfiles: [...prev.childProfiles, newProfile],
        activeChildProfile: newProfile, // Automatically select the new profile
        loading: false,
      }));
      
      return newProfile;
    } catch (error) {
      logger.error('Auth', 'Failed to create child profile', error);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to create child profile'
      }));
      
      throw error;
    }
  };
  
  // Set active child profile
  const setActiveChildProfile = async (childProfile) => {
    logger.trace('Auth', 'Setting active child profile', childProfile);
    setAuthState(prev => ({
      ...prev,
      loading: true,
    }));
    
    try {
      // If changing profiles, reset the WebSocket session first to leave any joined stories
      // This ensures a clean state for the new profile
      if (authState.activeChildProfile && authState.activeChildProfile.id !== childProfile.id) {
        logger.info('Auth', 'Resetting WebSocket session for profile switch');
        websocketService.resetSession();
      }
      
      // Set the active child profile in the backend
      const response = await api.post(`/child-profiles/${childProfile.id}/set-active/`);
      logger.trace('Auth', `Set child profile ${childProfile.id} as active in backend`, response.data);
      
      // Log the session key and any other useful information from the response
      if (response.data.session_key) {
        logger.trace('Auth', `Session key from backend: ${response.data.session_key}`);
      }
      
      // Log all cookies for debugging
      logger.trace('Auth', 'Cookies after setting active profile', document.cookie);
      
      // Get the session data to ensure session is updated
      const sessionResponse = await allauthApi.getSession();
      logger.trace('Auth', 'Session after setting active profile', sessionResponse);
      
      // Update WebSocket with the new profile's authentication parameters
      if (authState.user && authState.user.id) {
        try {
          const authParams = {
            userId: authState.user.id,
            profileId: childProfile.id
          };
          
          await websocketService.updateAuthParams(authParams);
          logger.info('Auth', 'WebSocket authentication parameters updated after profile change');
        } catch (wsError) {
          logger.error('Auth', 'Failed to update WebSocket auth parameters after profile change', wsError);
          // Continue anyway - the profile change was successful
        }
      }
      
      // Update the state
      setAuthState(prev => ({
        ...prev,
        activeChildProfile: childProfile,
        loading: false,
      }));
      
      // Additional logging of important state
      logger.info('Auth', 'Active child profile set successfully', {
        profileId: childProfile.id,
        profileName: childProfile.name,
        sessionData: response.data
      });
      
      return true;
    } catch (error) {
      logger.error('Auth', 'Failed to set active profile in backend', error);
      setAuthState(prev => ({
        ...prev,
        error: 'Failed to set active child profile',
        loading: false,
      }));
      
      return false;
    }
  };
  
  // Migrate session data to account
  const migrateSessionData = async (childProfileId) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await api.post(`/migrate-session/${childProfileId}/`);
      logger.info('Auth', 'Session data migration successful', response.data);
      
      // Reset anonymous activity after successful migration
      resetAnonymousActivity();
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
      }));
      
      return response.data;
    } catch (error) {
      logger.error('Auth', 'Failed to migrate session data', error);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to migrate session data'
      }));
      throw error;
    }
  };
  
  // Clear errors
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);
  
  // Track character creation for anonymous users
  const trackCharacterCreation = useCallback(() => {
    setAuthState(prev => {
      // Only increment for anonymous users
      if (prev.isAuthenticated) return prev;
      
      return {
        ...prev,
        anonymousActivity: {
          ...prev.anonymousActivity,
          charactersCreated: prev.anonymousActivity.charactersCreated + 1,
          hasContent: true
        }
      };
    });
    logger.trace('Auth', 'Tracked character creation for anonymous user');
  }, []);
  
  // Track story creation for anonymous users
  const trackStoryCreation = useCallback(() => {
    setAuthState(prev => {
      // Only increment for anonymous users
      if (prev.isAuthenticated) return prev;
      
      return {
        ...prev,
        anonymousActivity: {
          ...prev.anonymousActivity,
          storiesCreated: prev.anonymousActivity.storiesCreated + 1,
          hasContent: true
        }
      };
    });
    logger.trace('Auth', 'Tracked story creation for anonymous user');
  }, []);
  
  // Reset anonymous activity stats (e.g., after migration)
  const resetAnonymousActivity = useCallback(() => {
    setAuthState(prev => ({
      ...prev,
      anonymousActivity: {
        storiesCreated: 0,
        charactersCreated: 0,
        lastVisit: new Date().toISOString(),
        hasContent: false
      }
    }));
    localStorage.removeItem('anonymousActivity');
    logger.trace('Auth', 'Reset anonymous activity stats');
  }, []);
  
  // Create the context value
  const contextValue = {
    ...authState,
    login,
    logout,
    register,
    createChildProfile,
    setActiveChildProfile,
    migrateSessionData,
    completeTwoFactorAuth,
    verifyEmailWithKey,
    requestPasswordReset,
    resetPassword,
    clearError,
    refreshAuthState: loadAuthState,
    // Anonymous session tracking methods
    trackCharacterCreation,
    trackStoryCreation,
    resetAnonymousActivity,
  };
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;